<template>
  <div class="inline-block">
    <div v-if="showPhotoImage" class="overflow-hidden" :class="[computedClasses, { relative: showStatusOverlay }]">
      <img class="w-full h-full object-cover" :class="{ 'blur-sm': showStatusOverlay }" :src="imageUrl" :alt="avatarAlt" />
      <span
        v-if="statusOverlayLabel"
        class="absolute inset-0 flex items-center justify-center text-white tracking-wide text-center"
        :class="overlayTextClasses"
        aria-hidden="true"
      >
        {{ statusOverlayLabel }}
      </span>
    </div>
    <div
      v-else-if="photoUploadIcon"
      aria-hidden="true"
      class="bg-grey-divider-subdued text-grey-clickable font-medium flex items-center justify-center"
      :class="computedClasses"
    >
      <photo-icon :height="photoUploadDimensions.height" :width="photoUploadDimensions.width" color="#4B4C52" />
    </div>
    <div
      v-else
      aria-hidden="true"
      class="bg-grey-divider-subdued text-grey-clickable font-medium flex items-center justify-center"
      :class="computedClasses"
    >
      <span :class="textClasses">{{ placeholderInitials }}</span>
    </div>
  </div>
</template>

<script lang="ts">
import { PropType } from 'vue';
import PhotoIcon from '@/graphics/icons/PhotoIcon.vue';
import { PhotoStatus, photoStatusOverlayLabel, shouldDisplayOwnerPhoto } from '@/lib/constants/photo_status';

export enum AvatarSize {
  BASE = 'base',
  MEDIUM = 'medium',
  LARGE = 'large',
  XL = 'xl',
  MEGA = 'mega',
}

export default {
  name: 'Avatar',
  components: {
    PhotoIcon,
  },
  props: {
    imageUrl: {
      type: String || null,
      default: null,
    },
    size: {
      type: String as PropType<AvatarSize>,
      default: AvatarSize.BASE,
      validator: (value: string) => (Object.values(AvatarSize) as string[]).includes(value),
    },
    name: {
      type: String,
      required: true,
    },
    photoStatus: {
      type: String,
      default: PhotoStatus.APPROVED,
    },
    photoUploadIcon: {
      type: Boolean,
      default: false,
    },
    emptyAlt: {
      type: Boolean,
      default: false,
    },
    customClasses: {
      type: String,
      default: '',
    },
    rounded: {
      type: Boolean,
      default: true,
    },
  },
  computed: {
    computedClasses(): string {
      const baseClasses = this.customClasses || this.sizeClasses;
      const roundedClass = this.rounded ? ' rounded-full' : '';
      return `${baseClasses}${roundedClass}`;
    },
    textClasses(): string {
      if (this.size === AvatarSize.MEDIUM) {
        return 'text-m';
      }
      if (this.size === AvatarSize.LARGE || this.size === AvatarSize.XL) {
        return 'text-m2';
      }
      if (this.size === AvatarSize.MEGA) {
        return 'text-l';
      }
      return 'text-xs';
    },
    /** Status overlay label; capped at text-m so it fits smaller avatars. */
    overlayTextClasses(): string {
      if (this.size === AvatarSize.LARGE) {
        return 'text-[0.4rem]';
      }
      if (this.size === AvatarSize.XL) {
        return 'text-xxs';
      }
      if (this.size === AvatarSize.MEGA) {
        return 'text-m';
      }

      // Text is too small to read below the large size
      return 'hidden';
    },
    photoUploadDimensions(): any {
      if (this.size === AvatarSize.MEDIUM) {
        return { width: 16, height: 12 };
      }
      if (this.size === AvatarSize.LARGE) {
        return { width: 24, height: 18 };
      }
      if (this.size === AvatarSize.XL) {
        return { width: 32, height: 24 };
      }
      if (this.size === AvatarSize.MEGA) {
        return { width: 48, height: 36 };
      }
      return { width: 12, height: 9 };
    },
    sizeClasses(): string {
      if (this.size === AvatarSize.MEDIUM) {
        return 'w-32 h-32';
      }
      if (this.size === AvatarSize.LARGE) {
        return 'w-48 h-48';
      }
      if (this.size === AvatarSize.XL) {
        return 'w-64 h-64';
      }
      if (this.size === AvatarSize.MEGA) {
        return 'w-96 h-96';
      }
      return 'w-24 h-24';
    },
    hasImage(): boolean {
      return !!this.imageUrl;
    },
    showPhotoImage(): boolean {
      return shouldDisplayOwnerPhoto(this.photoStatus, this.imageUrl);
    },
    statusOverlayLabel(): string | null {
      if (!this.showPhotoImage) {
        return null;
      }
      return photoStatusOverlayLabel(this.photoStatus);
    },
    showStatusOverlay(): boolean {
      return this.statusOverlayLabel != null;
    },
    placeholderInitials(): string {
      if (!this.name) {
        return '';
      }
      const regex = /^([A-Za-z])[^\s]+\s+([A-Za-z])/;
      const m = this.name.match(regex);
      if (m === null) {
        return this.name.charAt(0).toUpperCase();
      }
      return m[1].toUpperCase() + m[2].toUpperCase();
    },
    avatarAlt(): string {
      if (this.emptyAlt) {
        return '';
      }
      if (this.statusOverlayLabel) {
        return `${this.statusOverlayLabel.toLowerCase()} avatar of ${this.name || ''}`;
      }
      return `Avatar of ${this.name || ''}`;
    },
  },
};
</script>
